import { EditableQuestions } from 'components/registration/v2/PostRegEditDrawer';
import * as React from 'react';
import {
  createContext,
  Dispatch,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import { getActions, PostRegDrawerActions } from './actions';
import { PostRegDrawerAction, postRegDrawerReducer } from './reducer';
import { getEditableQuestions } from './service';
import { ActionType, FormPage, LoadingType, PostRegDrawerState } from './types';

export const defaultPostRegDrawerState: PostRegDrawerState = {
  loading: {
    [LoadingType.GetEditableQuestions]: true,
    [LoadingType.GetPreviewLineItems]: false,
    [LoadingType.SubmitEdits]: false,
    [LoadingType.UploadFile]: false,
  },
  isFormValid: true,
  formResultId: null,
  orderId: null,
  registrantInfo: { fullName: '', email: '' },
  formStructure: null,
  questions: null,
  currentFormState: {},
  dirtyFields: [],
  formErrors: {},
  formPage: FormPage.Editing,
  pendingEdits: [],
  previewLineItems: null,
  pendingPayment: null,
  confirmation: null,
};

export const PostRegEditDrawerContext = createContext<PostRegDrawerState>(
  defaultPostRegDrawerState
);

const DispatchContext = createContext<Dispatch<PostRegDrawerAction>>(undefined);

export const PostRegEditDrawerProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [state, dispatch] = useReducer(
    postRegDrawerReducer,
    defaultPostRegDrawerState
  );

  return (
    <PostRegEditDrawerContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {children}
      </DispatchContext.Provider>
    </PostRegEditDrawerContext.Provider>
  );
};

export const usePostRegEditDrawer = (
  formResultId?: number,
  orderId?: number
): PostRegDrawerState => {
  const context = useContext(PostRegEditDrawerContext);
  const dispatch = useContext(DispatchContext);
  if (context === undefined || dispatch === undefined) {
    throw new Error(
      'usePostRegEditDrawer must be used within a PostRegEditDrawerProvider'
    );
  }

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!formResultId || context.formResultId === formResultId) {
        return;
      }
      try {
        dispatch({ type: ActionType.ResetState });
        dispatch({
          type: ActionType.SetFormResultId,
          payload: { formResultId, orderId },
        });
        dispatch({
          type: ActionType.SetLoading,
          loading: {
            loadingType: LoadingType.GetEditableQuestions,
            isLoading: true,
          },
        });
        const { data } = await getEditableQuestions(formResultId);
        if (data) {
          dispatch({
            type: ActionType.HydrateForm,
            payload: { questions: data as EditableQuestions },
          });
        }
        dispatch({
          type: ActionType.SetLoading,
          loading: {
            loadingType: LoadingType.GetEditableQuestions,
            isLoading: false,
          },
        });
      } catch (e) {
        dispatch({
          type: ActionType.SetLoading,
          loading: {
            loadingType: LoadingType.GetEditableQuestions,
            isLoading: false,
            error: e.message || 'An error occurred',
          },
        });
      }
    };

    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formResultId]);

  return context;
};

export const usePostRegEditActions = (): PostRegDrawerActions => {
  const state = useContext(PostRegEditDrawerContext);
  const dispatch = useContext(DispatchContext);
  if (dispatch === undefined) {
    throw new Error(
      'usePostRegEditActions must be used within a PostRegEditDrawerProvider'
    );
  }
  return getActions(state, dispatch);
};
